import {md5} from './md5'
import {base64Encode, base64Decode} from './base64'
import {nextRunTimes, describeCron} from './cron'

/**
 * 工具箱的能力注册表
 *
 * 与小程序端 weapp_tools/utils/tools 同源，逻辑保持一致。
 * 全部在浏览器里算完，输入内容不会发到服务器。
 *
 * kind 决定页面用哪种交互外壳：
 *   text  单输入框 + 若干动作按钮 + 输出
 *   dual  两个输入框（正则/被测文本、文本 A/文本 B）
 *   image 选图后本地压缩
 *   scan  从图片里识别二维码
 */

function requireInput(input, message) {
	if (!input || !String(input).trim()) {
		throw new Error(message || '请先输入内容')
	}
	return String(input)
}

const jsonTool = {
	key: 'json',
	kind: 'text',
	placeholder: '粘贴 JSON 内容',
	actions: [
		{key: 'format', label: '格式化', run: (input) => JSON.stringify(JSON.parse(requireInput(input)), null, 2)},
		{key: 'minify', label: '压缩', run: (input) => JSON.stringify(JSON.parse(requireInput(input)))},
		{
			key: 'validate',
			label: '校验',
			run: (input) => {
				const parsed = JSON.parse(requireInput(input))
				const type = Array.isArray(parsed) ? '数组' : typeof parsed === 'object' && parsed !== null ? '对象' : typeof parsed
				const size = Array.isArray(parsed) ? parsed.length
					: parsed && typeof parsed === 'object' ? Object.keys(parsed).length : 0
				return `语法正确\n顶层类型：${type}\n顶层成员数：${size}`
			}
		},
		{key: 'escape', label: '转义', run: (input) => JSON.stringify(requireInput(input))}
	]
}

const base64Tool = {
	key: 'base64',
	kind: 'text',
	placeholder: '输入要编码或解码的内容',
	actions: [
		{key: 'encode', label: '编码', run: (input) => base64Encode(requireInput(input))},
		{key: 'decode', label: '解码', run: (input) => base64Decode(requireInput(input))}
	]
}

const urlTool = {
	key: 'url',
	kind: 'text',
	placeholder: '输入 URL 或参数',
	actions: [
		{key: 'encode', label: '编码', run: (input) => encodeURIComponent(requireInput(input))},
		{key: 'decode', label: '解码', run: (input) => decodeURIComponent(requireInput(input))},
		{
			key: 'parse',
			label: '拆解参数',
			run: (input) => {
				const text = requireInput(input)
				const queryIndex = text.indexOf('?')
				const query = queryIndex > -1 ? text.slice(queryIndex + 1) : text
				const lines = query.split('&').filter(Boolean).map((pair) => {
					const index = pair.indexOf('=')
					const key = index > -1 ? pair.slice(0, index) : pair
					const value = index > -1 ? pair.slice(index + 1) : ''
					let decoded = value
					try {
						decoded = decodeURIComponent(value)
					} catch (e) {
						// 参数本身不是合法编码时保持原样
					}
					return `${key} = ${decoded}`
				})
				return lines.length ? lines.join('\n') : '没有解析到查询参数'
			}
		}
	]
}

const md5Tool = {
	key: 'md5',
	kind: 'text',
	placeholder: '输入要计算摘要的文本',
	actions: [
		{key: 'lower', label: '小写', run: (input) => md5(requireInput(input))},
		{key: 'upper', label: '大写', run: (input) => md5(requireInput(input)).toUpperCase()},
		{key: 'short', label: '16 位', run: (input) => md5(requireInput(input)).slice(8, 24)}
	]
}

const uuidTool = {
	key: 'uuid',
	kind: 'text',
	placeholder: '可留空，或输入要生成的数量',
	needInput: false,
	actions: [
		{key: 'v4', label: '生成 UUID', run: (input) => range(clampCount(input)).map(() => uuidV4()).join('\n')},
		{key: 'simple', label: '去横线', run: (input) => range(clampCount(input)).map(() => uuidV4().replace(/-/g, '')).join('\n')},
		{
			key: 'random',
			label: '随机串',
			run: (input) => {
				const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
				return range(clampCount(input)).map(() => range(16)
					.map(() => chars[Math.floor(Math.random() * chars.length)]).join('')).join('\n')
			}
		}
	]
}

const radixTool = {
	key: 'radix',
	kind: 'text',
	placeholder: '输入数值，如 255、0xff、0b1010',
	actions: [
		{
			key: 'all',
			label: '转换',
			run: (input) => {
				const value = parseNumber(requireInput(input).trim())
				return [
					`十进制：${value}`,
					`二进制：${value.toString(2)}`,
					`八进制：${value.toString(8)}`,
					`十六进制：${value.toString(16).toUpperCase()}`
				].join('\n')
			}
		}
	]
}

const colorTool = {
	key: 'color',
	kind: 'text',
	placeholder: '输入 #4f6ef7 或 rgb(79,110,247)',
	actions: [
		{
			key: 'convert',
			label: '转换',
			run: (input) => {
				const {r, g, b} = parseColor(requireInput(input))
				const hex = '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
				const {h, s, l} = rgbToHsl(r, g, b)
				return [
					`HEX：${hex.toUpperCase()}`,
					`RGB：rgb(${r}, ${g}, ${b})`,
					`HSL：hsl(${h}, ${s}%, ${l}%)`
				].join('\n')
			}
		}
	]
}

const textTool = {
	key: 'text',
	kind: 'text',
	placeholder: '输入或粘贴文本',
	actions: [
		{
			key: 'count',
			label: '统计',
			run: (input) => {
				const text = requireInput(input)
				const lines = text.split('\n')
				const chineseCount = (text.match(/[\u4e00-\u9fa5]/g) || []).length
				return [
					`字符数：${text.length}`,
					`不含空白：${text.replace(/\s/g, '').length}`,
					`中文字符：${chineseCount}`,
					`行数：${lines.length}`,
					`非空行：${lines.filter((line) => line.trim()).length}`
				].join('\n')
			}
		},
		{key: 'dedupe', label: '按行去重', run: (input) => Array.from(new Set(requireInput(input).split('\n'))).join('\n')},
		{key: 'sort', label: '按行排序', run: (input) => requireInput(input).split('\n').sort().join('\n')},
		{key: 'upper', label: '转大写', run: (input) => requireInput(input).toUpperCase()},
		{key: 'lower', label: '转小写', run: (input) => requireInput(input).toLowerCase()},
		{key: 'trim', label: '去空行', run: (input) => requireInput(input).split('\n').filter((line) => line.trim()).join('\n')}
	]
}

const cronTool = {
	key: 'cron',
	kind: 'text',
	placeholder: '输入表达式，如 0 5 0 * * ?',
	actions: [
		{
			key: 'next',
			label: '下次执行',
			run: (input) => {
				const times = nextRunTimes(requireInput(input, '请输入 Cron 表达式'), 5)
				return times.length ? times.join('\n') : '未来四年内不会再触发'
			}
		},
		{key: 'describe', label: '字段说明', run: (input) => describeCron(requireInput(input, '请输入 Cron 表达式'))}
	]
}

const timestampTool = {
	key: 'timestamp',
	kind: 'text',
	placeholder: '留空取当前时间，或输入 1735689600 / 2026-01-01 12:00:00',
	needInput: false,
	actions: [
		{
			key: 'toDate',
			label: '时间戳转日期',
			run: (input) => {
				const text = String(input || '').trim()
				const ms = text ? toMillis(text) : Date.now()
				return formatAll(new Date(ms))
			}
		},
		{
			key: 'toTimestamp',
			label: '日期转时间戳',
			run: (input) => {
				const text = String(input || '').trim()
				const date = text ? parseDateText(text) : new Date()
				return [
					`秒：${Math.floor(date.getTime() / 1000)}`,
					`毫秒：${date.getTime()}`,
					`ISO：${date.toISOString()}`
				].join('\n')
			}
		},
		{key: 'now', label: '当前时间', run: () => formatAll(new Date())}
	]
}

const regexTool = {
	key: 'regex',
	kind: 'dual',
	primaryLabel: '正则表达式',
	primaryPlaceholder: '例如 \\d{3}-\\d{4}',
	secondaryLabel: '被测文本',
	secondaryPlaceholder: '粘贴要匹配的文本',
	flagOptions: ['g', 'i', 'm', 's'],
	actions: [
		{
			key: 'match',
			label: '匹配',
			run: (pattern, text, flags) => {
				requireInput(pattern, '请输入正则表达式')
				requireInput(text, '请输入被测文本')
				const regex = new RegExp(pattern, flags.indexOf('g') > -1 ? flags : flags + 'g')
				const matches = Array.from(text.matchAll(regex))
				if (!matches.length) {
					return '没有匹配到任何内容'
				}
				return matches.map((m, i) => {
					const groups = m.length > 1 ? '\n    分组：' + m.slice(1).map((g, gi) => `$${gi + 1}=${g}`).join('  ') : ''
					return `[${i + 1}] 位置 ${m.index}：${m[0]}${groups}`
				}).join('\n')
			}
		},
		{
			key: 'test',
			label: '是否匹配',
			run: (pattern, text, flags) => {
				requireInput(pattern, '请输入正则表达式')
				return new RegExp(pattern, flags.replace('g', '')).test(text) ? '匹配成功' : '不匹配'
			}
		},
		{
			key: 'count',
			label: '统计数量',
			run: (pattern, text, flags) => {
				requireInput(pattern, '请输入正则表达式')
				const regex = new RegExp(pattern, flags.indexOf('g') > -1 ? flags : flags + 'g')
				return `共匹配到 ${Array.from(String(text).matchAll(regex)).length} 处`
			}
		}
	]
}

const diffTool = {
	key: 'diff',
	kind: 'dual',
	primaryLabel: '文本 A',
	primaryPlaceholder: '粘贴原始文本',
	secondaryLabel: '文本 B',
	secondaryPlaceholder: '粘贴对比文本',
	actions: [
		{
			key: 'diff',
			label: '逐行对比',
			run: (a, b) => {
				const linesA = String(a || '').split('\n')
				const linesB = String(b || '').split('\n')
				const max = Math.max(linesA.length, linesB.length)
				const result = []
				let same = 0
				for (let i = 0; i < max; i++) {
					const la = linesA[i]
					const lb = linesB[i]
					if (la === lb) {
						same++
						continue
					}
					if (la === undefined) {
						result.push(`第 ${i + 1} 行  + ${lb}`)
					} else if (lb === undefined) {
						result.push(`第 ${i + 1} 行  - ${la}`)
					} else {
						result.push(`第 ${i + 1} 行  - ${la}\n         + ${lb}`)
					}
				}
				if (!result.length) {
					return `两段文本完全一致，共 ${max} 行`
				}
				return `共 ${max} 行，相同 ${same} 行，差异 ${result.length} 处\n\n${result.join('\n')}`
			}
		}
	]
}

const imageTool = {key: 'image', kind: 'image'}

const scanTool = {key: 'scan', kind: 'scan'}

const TOOLS = [
	jsonTool, timestampTool, regexTool, base64Tool, urlTool, md5Tool, uuidTool,
	radixTool, colorTool, textTool, cronTool, diffTool, imageTool, scanTool
]

const TOOL_MAP = TOOLS.reduce((map, tool) => {
	map[tool.key] = tool
	return map
}, {})

export function getTool(key) {
	return TOOL_MAP[key] || null
}

export default TOOLS

// ------------------------------ 内部辅助 ------------------------------

function range(n) {
	return Array.from({length: n}, (_, i) => i)
}

function clampCount(input) {
	const parsed = parseInt(String(input || '').trim(), 10)
	if (isNaN(parsed) || parsed < 1) {
		return 1
	}
	return Math.min(parsed, 50)
}

function uuidV4() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0
		const v = c === 'x' ? r : (r & 0x3) | 0x8
		return v.toString(16)
	})
}

function parseNumber(text) {
	let value
	if (/^0x[0-9a-f]+$/i.test(text)) {
		value = parseInt(text.slice(2), 16)
	} else if (/^0b[01]+$/i.test(text)) {
		value = parseInt(text.slice(2), 2)
	} else if (/^0o[0-7]+$/i.test(text)) {
		value = parseInt(text.slice(2), 8)
	} else if (/^-?\d+$/.test(text)) {
		value = parseInt(text, 10)
	} else {
		throw new Error('无法识别的数值，支持 255、0xff、0b1010、0o777')
	}
	if (isNaN(value)) {
		throw new Error('数值解析失败')
	}
	return value
}

function parseColor(text) {
	const trimmed = text.trim()

	const hexMatch = trimmed.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
	if (hexMatch) {
		let hex = hexMatch[1]
		if (hex.length === 3) {
			hex = hex.split('').map((c) => c + c).join('')
		}
		return {
			r: parseInt(hex.slice(0, 2), 16),
			g: parseInt(hex.slice(2, 4), 16),
			b: parseInt(hex.slice(4, 6), 16)
		}
	}

	const rgbMatch = trimmed.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
	if (rgbMatch) {
		const parts = [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map((v) => parseInt(v, 10))
		if (parts.some((v) => v < 0 || v > 255)) {
			throw new Error('RGB 分量必须在 0-255 之间')
		}
		return {r: parts[0], g: parts[1], b: parts[2]}
	}

	throw new Error('无法识别的颜色，支持 #4f6ef7 或 rgb(79,110,247)')
}

function rgbToHsl(r, g, b) {
	const rn = r / 255
	const gn = g / 255
	const bn = b / 255
	const max = Math.max(rn, gn, bn)
	const min = Math.min(rn, gn, bn)
	const delta = max - min

	let h = 0
	if (delta !== 0) {
		if (max === rn) {
			h = ((gn - bn) / delta) % 6
		} else if (max === gn) {
			h = (bn - rn) / delta + 2
		} else {
			h = (rn - gn) / delta + 4
		}
		h = Math.round(h * 60)
		if (h < 0) h += 360
	}

	const l = (max + min) / 2
	const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

	return {h, s: Math.round(s * 100), l: Math.round(l * 100)}
}

/** 10 位按秒、13 位按毫秒 */
function toMillis(text) {
	if (!/^-?\d+$/.test(text)) {
		throw new Error('时间戳只能是数字，日期请用「日期转时间戳」')
	}
	const num = parseInt(text, 10)
	return text.replace('-', '').length <= 10 ? num * 1000 : num
}

function parseDateText(text) {
	// Safari 不认 "2026-01-01 12:00:00" 这种带空格的写法
	const date = new Date(text.replace(/-/g, '/'))
	if (isNaN(date.getTime())) {
		throw new Error('无法识别的日期，支持 2026-01-01 或 2026-01-01 12:00:00')
	}
	return date
}

function formatAll(date) {
	const pad = (n) => String(n).padStart(2, '0')
	const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
		+ `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
	const week = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()]
	return [
		`本地时间：${local} ${week}`,
		`UTC：${date.toISOString().replace('T', ' ').slice(0, 19)}`,
		`秒：${Math.floor(date.getTime() / 1000)}`,
		`毫秒：${date.getTime()}`
	].join('\n')
}
